import React from 'react';
import styled from 'styled-components';

import { hasElements } from '../../../../../utils/variables';
import { ViewSettings } from '../../../../../viewer/view-settings';
import { CameraControlTool } from '../../camera-control-toggle';
import { useCameraViewTools } from '../../camera-views';
import { FocusToolIcon } from '../../toolbar/tools/focus-tool-icon';
import { FlexibleCameraTool } from '../../toolbar/tools/view-settings/flexible-camera-tool';
import { useClassicViewTools } from '../../toolbar/tools/view-settings/use-classic-view-tools';
import { ViewSettingsTool } from '../../toolbar/tools/view-settings/view-settings-tool';

type Props = {
  /** Whether the timeline is before them, to keep apart from. */
  afterTimeline: boolean;
  /** The notch, which the view settings open below. */
  notchRef: React.RefObject<HTMLElement>;
};

/**
 * The views in the timeline bar's notch, beside the timeline they show: the cameras in the CAD
 * viewer and the flexible viewing mode, else a view per layer as in the toolbar, then the view
 * settings, the focus and the camera control.
 */
const TimelineBarViews = ({ afterTimeline, notchRef }: Props) => {
  const {
    camera,
    hasPerspective,
    hasOrthographic,
    canChooseCameraControl,
    hasFocus,
    hasViewSettings,
  } = useCameraViewTools();
  const classicViewTools = useClassicViewTools(notchRef);
  const showsCameras = ViewSettings.instance.showsCameras;

  const views = showsCameras
    ? [
        hasPerspective && <FlexibleCameraTool camera="perspective" key="perspective" />,
        hasOrthographic && <FlexibleCameraTool camera="orthographic" key="orthographic" />,
      ].filter(Boolean)
    : classicViewTools;
  const hasSettings = showsCameras
    ? hasViewSettings
    : ViewSettings.instance.isEnabled && hasElements(views);

  const hasTools = hasSettings || hasFocus || canChooseCameraControl;

  if (!hasElements(views) && !hasTools) {
    return null;
  }

  return (
    <Component>
      {hasElements(views) && (
        <>
          {afterTimeline && <Separator />}
          <Views>{views}</Views>
        </>
      )}
      {hasTools && (afterTimeline || hasElements(views)) && <Separator />}
      {hasSettings && <ViewSettingsTool placement="bottom" toolbarRef={notchRef} />}
      {hasFocus && <FocusToolIcon />}
      {canChooseCameraControl && <CameraControlTool disabled={camera !== 'perspective'} />}
    </Component>
  );
};

// The toolbar's tool icons, smaller to fit the notch
const Component = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;

  svg[data-active] {
    font-size: 18px;
  }
`;

// The views together, as one choice
const Views = styled.div`
  display: flex;
  gap: 2px;
  padding: 2px;
  border-radius: 8px;
  background-color: var(--hover);
`;

const Separator = styled.span`
  width: 1px;
  height: 12px;
  margin: 0 6px;
  background-color: var(--separator);
`;

export { TimelineBarViews };
